import { signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

export type SocketConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed';

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

export abstract class ReconnectingSocket<TEvent> {

  public readonly connectionStatus = signal<SocketConnectionStatus>('idle');
  public readonly message$ = new Subject<TEvent>();

  private socket$: WebSocketSubject<TEvent> | null = null;
  private socketSubscription: Subscription | null = null;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
  private shouldReconnect = false;

  protected connect(url: string): void {
    this.disconnect();
    this.shouldReconnect = true;
    this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    this.openSocket(url);
  }

  protected disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimeout();
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = null;
    this.socket$?.complete();
    this.socket$ = null;
    this.connectionStatus.set('idle');
  }

  private openSocket(url: string): void {
    this.connectionStatus.set('connecting');

    this.socket$ = webSocket<TEvent>({
      url,
      openObserver: {
        next: () => {
          this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
          this.connectionStatus.set('open');
        }
      },
      closeObserver: {
        next: () => this.handleDisconnect(url)
      }
    });

    this.socketSubscription = this.socket$.subscribe({
      next: (event) => this.message$.next(event),
      error: () => this.handleDisconnect(url)
    });
  }

  private handleDisconnect(url: string): void {
    this.connectionStatus.set('closed');

    if (!this.shouldReconnect) {
      return;
    }

    this.clearReconnectTimeout();
    this.reconnectTimeoutId = setTimeout(() => this.openSocket(url), this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }
}
